import React, { useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { CustomButton } from '@/components/molecules/CustomButton';
import { CustomInput } from '@/components/molecules/CustomInput';
import { useSocket } from '@/hooks/useSocketContext';
import { parseMinecraftText } from '@/utils/minecraftHelper';
import { sendMessageToBluemap } from '@/services/bluemap.ts';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: string;
  author?: string;
  provider?: string;
}

const renderMinecraftText = (rawText: string) =>
  parseMinecraftText(rawText).map((part, index) => (
    <span
      key={`${index}-${part.text}`}
      style={{
        color: part.color,
        fontWeight: part.bold ? 700 : undefined,
        fontStyle: part.italic ? 'italic' : undefined,
        textDecoration:
          `${part.underline ? 'underline ' : ''}${part.strike ? 'line-through' : ''}`.trim() ||
          undefined,
      }}
    >
      {part.text}
    </span>
  ));

export function BlueMapViewer() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isChatOpen, setIsChatOpen] = useState<boolean>(true);
  const { isConnected, isConnecting, emit, on } = useSocket();

  const bluemapUrl = `${import.meta.env.VITE_API_URL}/api/bluemap/view#ville1`;

  useEffect(() => {
    const removeMinecraftListener = on('minecraft:chat', (payload) => {
      const parsed = payload as {
        author?: string;
        message?: string;
        provider?: string;
        sentAt?: string;
      };

      const message = parsed?.message ?? '';
      if (!message.trim()) {
        return;
      }

      const sentAt = parsed.sentAt ? new Date(parsed.sentAt) : new Date();
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: message,
          sender: 'assistant',
          author: parsed.author,
          provider: parsed.provider,
          timestamp: sentAt.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
      ]);
    });

    return () => {
      removeMinecraftListener();
    };
  }, [on]);

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!inputMessage.trim()) {
      return;
    }

    const outgoingMessage = inputMessage;
    await sendMessageToBluemap(outgoingMessage);

    emit('chat_message', {
      message: outgoingMessage,
    });
    setInputMessage('');
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-gray-900">
      <iframe
        src={bluemapUrl}
        className="h-full w-full border-0"
        title="BlueMap Viewer"
        allow="fullscreen"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-presentation"
      />

      {!isChatOpen && (
        <CustomButton
          onClick={() => setIsChatOpen(true)}
          icon="message-square"
          color="background"
          iconOnly
          floating
          rounded="full"
          shadow="2xl"
          size="lg"
          ariaLabel="Ouvrir le chat"
        />
      )}

      {isChatOpen && (
        <div className="bg-surface/95 fixed right-3 bottom-4 z-50 flex h-[min(32rem,calc(100vh-2rem))] w-[min(24rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl shadow-2xl backdrop-blur-sm md:right-6 md:bottom-6">
          <div className="bg-raised text-content flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-surface/15 p-1.5">
                <MessageSquare size={18} />
              </span>
              <div>
                <h3 className="font-lexend text-sm leading-none font-semibold">
                  Chat
                </h3>
                <p className="mt-1 text-[11px] leading-none">
                  {isConnected
                    ? 'Socket connecte'
                    : isConnecting
                      ? 'Connexion socket...'
                      : 'Socket deconnecte'}
                </p>
              </div>
            </div>
            <CustomButton
              onClick={() => setIsChatOpen(false)}
              icon="x"
              iconOnly
              size="sm"
              iconSize={18}
              rounded="md"
              variant="ghost"
              color="dark"
              ariaLabel="Fermer le chat"
            />
          </div>

          <div className="bg-raised/80 flex-1 space-y-3 overflow-y-auto p-4">
            {messages.length === 0 ? (
              <div className="mx-auto mt-8 max-w-xs rounded-xl bg-surface px-4 py-5 text-center shadow-sm">
                <p className="text-content font-lexend text-sm font-semibold">
                  Bienvenue
                </p>
                <p className="text-content/80 mt-2 text-sm">
                  Discutez pendant que vous explorez la carte.
                </p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm ${
                      msg.sender === 'user'
                        ? 'bg-primary-500 text-white'
                        : 'border-primary-100 text-content border bg-surface'
                    }`}
                  >
                    {msg.author && (
                      <p className="mb-1 text-xs font-semibold opacity-80">
                        {renderMinecraftText(msg.author)}
                      </p>
                    )}
                    <p className="text-sm wrap-break-word">
                      {renderMinecraftText(msg.text)}
                    </p>
                    <span
                      className={`mt-1 block text-[11px] ${
                        msg.sender === 'user'
                          ? 'text-white/75'
                          : 'text-content/60'
                      }`}
                    >
                      {msg.timestamp}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <form onSubmit={handleSendMessage} className="bg-white px-3 py-3">
            <div className="flex items-center gap-2">
              <CustomInput
                type="text"
                value={inputMessage}
                onChange={setInputMessage}
                placeholder="Tapez votre message..."
                autoComplete="off"
                variant="chat"
                rounded="xl"
                size="md"
                fullWidth
                className="text-sm"
              />
              <CustomButton
                type="submit"
                icon="send"
                iconOnly
                size="md"
                iconSize={18}
                color="primary"
                rounded="md"
                disabled={!inputMessage.trim()}
                ariaLabel="Envoyer le message"
              />
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
